import {
  formatQuantity,
  priceListInputSchema,
  priceListItemInputSchema,
  priceListUpdateSchema,
  type Page,
  type PriceList,
  type PriceListItem,
  type Product,
} from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useCan } from "@/app/me";
import { apiFetch } from "@/lib/api";
import { notifySuccess } from "@/lib/notice";
import { toDecimal } from "@/lib/decimal";
import { issuesToErrors, str, useCadastroList, useForm, useListState, useSave } from "./lib";
import { Checkbox, ListToolbar, Pager, SaveError, StatusBadge, SearchInput } from "./widgets";

const money = (v: string | number) =>
  Number(v).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

export function PriceListsPage() {
  const canEdit = useCan()("cadastros.precos.gerenciar");
  const ls = useListState();
  const [editing, setEditing] = useState<PriceList | "new" | null>(null);
  const [selected, setSelected] = useState<PriceList | null>(null);
  const list = useCadastroList<PriceList>("tabelas-preco", ls);

  return (
    <div className="space-y-6">
      <PageTitle
        title="Tabelas de preço"
        description="Preço por produto e por unidade (pacote, fardo, caixa). A tabela padrão vale para quem não tem outra."
        actions={
          canEdit && (
            <Button onClick={() => setEditing("new")} disabled={editing === "new"}>
              Nova tabela
            </Button>
          )
        }
      />
      {editing && (
        <PriceListEditor
          key={editing === "new" ? "new" : editing.id}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Card>
        <ListToolbar
          q={ls.q}
          onQ={ls.setQ}
          status={ls.status}
          onStatus={ls.setStatus}
          placeholder="Buscar por nome"
        />
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Nome", "Validade", "Preços", "Situação", ""]}>
              {list.data?.items.map((l) => (
                <tr key={l.id} className={selected?.id === l.id ? "bg-muted/60" : undefined}>
                  <td className="px-3 py-2">
                    <span className="font-medium">{l.name}</span>{" "}
                    {l.isDefault && <Badge tone="ok">padrão</Badge>}
                  </td>
                  <td className="px-3 py-2">
                    {l.validFrom || l.validUntil
                      ? `${l.validFrom ? new Date(`${l.validFrom}T12:00`).toLocaleDateString("pt-BR") : "…"} a ${
                          l.validUntil
                            ? new Date(`${l.validUntil}T12:00`).toLocaleDateString("pt-BR")
                            : "…"
                        }`
                      : "Sem prazo"}
                  </td>
                  <td className="text-right tabular-nums px-3 py-2">{l.itemCount}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={l.status} />
                  </td>
                  <td className="space-x-1 px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(l)}>
                      Preços
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(l)}>
                        Editar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={ls.offset} onOffset={ls.setOffset} />
          </>
        )}
      </Card>
      {selected && <PriceItems key={selected.id} list={selected} canEdit={canEdit} />}
    </div>
  );
}

function PriceListEditor({ item, onClose }: { item: PriceList | null; onClose: () => void }) {
  const f = useForm({
    name: str(item?.name),
    description: str(item?.description),
    validFrom: str(item?.validFrom),
    validUntil: str(item?.validUntil),
    isDefault: item?.isDefault ?? false,
    status: item?.status ?? "active",
  });
  const save = useSave<unknown, PriceList>({
    resource: "tabelas-preco",
    id: item?.id ?? null,
    invalidate: ["tabelas-preco", "opcoes"],
    onSaved: onClose,
    successMessage: (_r, created) =>
      created ? "Tabela de preço criada com sucesso." : "Tabela de preço atualizada com sucesso.",
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = item
      ? priceListUpdateSchema.safeParse({ ...f.values, version: item.version })
      : priceListInputSchema.safeParse(f.values);
    if (!parsed.success) return f.setErrors(issuesToErrors(parsed.error.issues));
    f.setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <Card title={item ? `Editar ${item.name}` : "Nova tabela de preço"}>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-4">
        <Field label="Nome" error={f.errors["name"]} required>
          <Input {...f.text("name")} />
        </Field>
        <Field label="Válida a partir de" error={f.errors["validFrom"]}>
          <Input type="date" {...f.text("validFrom")} />
        </Field>
        <Field label="Válida até" error={f.errors["validUntil"]}>
          <Input type="date" {...f.text("validUntil")} />
        </Field>
        <Field label="Situação" error={f.errors["status"]}>
          <Select {...f.text("status")}>
            <option value="active">Ativa</option>
            <option value="inactive">Inativa</option>
          </Select>
        </Field>
        <div className="sm:col-span-3">
          <Field label="Descrição" error={f.errors["description"]}>
            <Input {...f.text("description")} />
          </Field>
        </div>
        <div className="flex items-end pb-2">
          <Checkbox label="Tabela padrão da empresa" {...f.check("isDefault")} />
        </div>
        <div className="flex gap-2 sm:col-span-4">
          <Button type="submit" disabled={save.isPending}>
            Salvar
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        <div className="sm:col-span-4">
          <SaveError error={save.error} />
        </div>
      </form>
    </Card>
  );
}

function PriceItems({ list, canEdit }: { list: PriceList; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const items = useQuery({
    queryKey: ["cadastros", "tabelas-preco", list.id, "itens"],
    queryFn: () => apiFetch<PriceListItem[]>(`/cadastros/tabelas-preco/${list.id}/itens`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["cadastros", "tabelas-preco"] });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/cadastros/tabelas-preco/${list.id}/itens/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const term = q.trim().toLowerCase();
  const rows = (items.data ?? []).filter(
    (i) =>
      !term ||
      i.productName.toLowerCase().includes(term) ||
      i.productCode.toLowerCase().includes(term),
  );

  return (
    <Card title={`Preços — ${list.name}`}>
      {canEdit && <AddPrice listId={list.id} onSaved={refresh} />}
      <div className="my-4">
        <Input
          className="max-w-sm"
          placeholder="Filtrar produto"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Filtrar produto"
        />
      </div>
      {items.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum preço nesta tabela.</p>
      ) : (
        <Table head={["Produto", "Unidade", "Preço", "Equivale a", "Desconto máx.", ""]}>
          {rows.map((i) => (
            <tr key={i.id}>
              <td className="px-3 py-2">
                <span className="font-mono text-xs text-muted-foreground">{i.productCode}</span>{" "}
                {i.productName}
              </td>
              <td className="px-3 py-2 font-mono">
                {i.unitCode}
                {i.unitCode !== i.baseUnitCode && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({formatQuantity(i.factor)} {i.baseUnitCode})
                  </span>
                )}
              </td>
              <td className="text-right tabular-nums px-3 py-2 font-medium">{money(i.price)}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {i.unitCode !== i.baseUnitCode && Number(i.factor) > 0
                  ? `${money(Number(i.price) / Number(i.factor))} / ${i.baseUnitCode}`
                  : "—"}
              </td>
              <td className="text-right tabular-nums px-3 py-2">
                {i.maxDiscountPercent ? `${Number(i.maxDiscountPercent)}%` : "—"}
              </td>
              <td className="px-3 py-2 text-right">
                {canEdit && (
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(i.id)}>
                    Remover
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <div className="mt-3">
        <SaveError error={remove.error} />
      </div>
    </Card>
  );
}

/** Define (ou substitui) o preço de um produto numa unidade. Repetir é seguro. */
function AddPrice({ listId, onSaved }: { listId: string; onSaved: () => void }) {
  const [search, setSearch] = useState("");
  const [productId, setProductId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const found = useQuery({
    queryKey: ["cadastros", "produtos", "busca", search],
    queryFn: () =>
      apiFetch<Page<Product>>(
        `/cadastros/produtos?limit=20&q=${encodeURIComponent(search.trim())}`,
      ),
    enabled: search.trim().length >= 2,
  });
  const product = useQuery({
    queryKey: ["cadastros", "produtos", "detalhe", productId],
    queryFn: () => apiFetch<Product>(`/cadastros/produtos/${productId}`),
    enabled: productId !== "",
  });
  const unitsOfProduct = product.data
    ? [
        { id: product.data.baseUnitId, code: product.data.baseUnitCode, factor: "1" },
        ...(product.data.packagings ?? [])
          .filter((p) => p.status === "active")
          .map((p) => ({ id: p.unitId, code: p.unitCode, factor: p.factor })),
      ]
    : [];
  const chosen = unitsOfProduct.find((u) => u.id === unitId);

  const save = useMutation({
    mutationFn: (body: unknown) =>
      apiFetch<PriceListItem>(`/cadastros/tabelas-preco/${listId}/itens`, { method: "POST", body }),
    onSuccess: () => {
      setPrice("");
      setDiscount("");
      notifySuccess("Preço salvo com sucesso.");
      onSaved();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = priceListItemInputSchema.safeParse({
      productId,
      unitId,
      price,
      maxDiscountPercent: discount,
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    save.mutate(parsed.data);
  }

  // Prévia apenas visual; o preço gravado é exatamente o digitado.
  const priceNumber = Number(toDecimal(price));
  const perBase =
    chosen && chosen.factor !== "1" && price && Number.isFinite(priceNumber)
      ? `${money(priceNumber / Number(chosen.factor))} por ${product.data?.baseUnitCode}`
      : undefined;

  return (
    <form
      onSubmit={submit}
      className="grid gap-3 rounded-md border p-3 sm:grid-cols-6 sm:items-end"
    >
      <div className="sm:col-span-2">
        <Field label="Produto" hint="Digite ao menos 2 letras do código ou nome">
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setProductId("");
              setUnitId("");
            }}
            placeholder="Buscar produto"
          />
        </Field>
        {search.trim().length >= 2 && !productId && (
          <ul className="mt-1 max-h-48 overflow-auto rounded-md border bg-card text-sm">
            {found.data?.items.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="w-full px-3 py-1.5 text-left hover:bg-muted"
                  onClick={() => {
                    setProductId(p.id);
                    setSearch(`${p.code} — ${p.name}`);
                  }}
                >
                  <span className="font-mono text-xs">{p.code}</span> {p.name}
                </button>
              </li>
            ))}
            {found.data && found.data.items.length === 0 && (
              <li className="px-3 py-1.5 text-muted-foreground">Nenhum produto encontrado.</li>
            )}
          </ul>
        )}
      </div>
      <Field label="Unidade">
        <Select value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={!product.data}>
          <option value="">Selecione</option>
          {unitsOfProduct.map((u) => (
            <option key={u.id} value={u.id}>
              {u.code}
              {u.factor !== "1"
                ? ` (${formatQuantity(u.factor)} ${product.data?.baseUnitCode})`
                : " (base)"}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Preço (R$)" hint={perBase}>
        <Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
      </Field>
      <Field label="Desconto máx. (%)">
        <Input inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} />
      </Field>
      <Button type="submit" disabled={save.isPending}>
        Salvar preço
      </Button>
      <div className="sm:col-span-6">
        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={save.error} />
      </div>
    </form>
  );
}
